import { getCif, getGuid } from '@ally/data-selectors-customer-info'
import * as accountSelector from '@ally/data-selectors-accounts'
import { outage } from '@ally/data-selectors-accounts'
import { Domains, HostData } from '@ally/data-types-host'
import { Analytics, SessionData } from '@ally/federated-types'
import { getBooleanSessionStorage } from '@ally/utilitarian'
import logRocket from 'logrocket'
import { CustomizationDomain, Widget } from '@ally/data-types-customization'
import { Account, AccountMeta } from '@ally/data-types-afg-accounts'
import { StatusTrackerActivity } from '@ally/shared-status-tracker'
import { LoadState } from '@ally/use-data-domain'
import { DashboardView } from './CustomizeDashboard/types'
import { filterActiveDepositAccounts, isAccountBlocked } from './utils'
import {
  BANK_ACCOUNT_TYPES,
  BANK_ACCOUNT_TYPES_MAP,
  INVEST_ACCOUNT_TYPES,
  INVEST_ACCOUNT_TYPES_MAP,
} from './constants'

export const trackCustomizationSettings = (
  customizeData: CustomizationDomain,
  eligibleWidgets: Widget[],
  dashboardView: DashboardView,
  status?: string[],
): void => {
  const lobOrder = customizeData.map(data =>
    [...data.lobs].sort((a, b) => a.order - b.order).map(lob => lob.name),
  )
  const accountOrder = {
    invest: customizeData.map(data =>
      [...data.accountTypes]
        .sort((a, b) => a.order - b.order)
        .filter(account => INVEST_ACCOUNT_TYPES.includes(account.name))
        .map(account => INVEST_ACCOUNT_TYPES_MAP[account.name]),
    ),
    bank: customizeData.map(data =>
      [...data.accountTypes]
        .sort((a, b) => a.order - b.order)
        .filter(account => BANK_ACCOUNT_TYPES.includes(account.name))
        .map(account => BANK_ACCOUNT_TYPES_MAP[account.name]),
    ),
  }
  const accountPosition = {
    lobOrder,
    accountOrder,
  }

  const widgetOrder = [...eligibleWidgets]
    .sort((a, b) => a.order - b.order)
    .map(widget => widget.name)

  try {
    ;(window as any).allytm.event('impressionEvent', {
      name: 'dashboardImpressionEvent',
      contents: [
        {
          name: 'Customized Dash Impression',
          type: 'dashboardCustomization',
          homeLoansStatus: status,
          attributes: {
            accountPosition,
            widgetPosition: dashboardView,
            widgetOrder,
          },
        },
      ],
    })
  } catch (e) {
    logRocket.captureException(e as Error)
  }
}

export const pageViewAnalytics = (
  analytics: Analytics,
  hostData: HostData,
  sessionData: SessionData,
  isMobile: boolean,
  eligibleWidgets: Widget[],
  dashboardView: DashboardView,
): void => {
  const { trackPageView } = analytics
  const customizationDomain = hostData[Domains.CUSTOMIZATION]
  const hasCustomizationError =
    customizationDomain.status.loadState === LoadState.FAILED
  let isDashboardCustomized = false
  if (!hasCustomizationError) {
    const hasVisibleWidgets = eligibleWidgets.length > 0
    const hasCustomizationApiData = Array.isArray(customizationDomain.data)
      ? (customizationDomain.data as CustomizationDomain).length > 0
      : false
    isDashboardCustomized = hasVisibleWidgets || hasCustomizationApiData
  }

  const internalAccounts = accountSelector.deposit.getAll(hostData) || []
  const activeInternalAccounts = filterActiveDepositAccounts(internalAccounts)
  const afgAccounts = hostData?.afgAccounts?.data?.data
  const accounts = activeInternalAccounts.map(account =>
    (({
      accountType,
      accountNickname,
      availableBalancePvtEncrypt,
      currentBalancePvtEncrypt,
      interestYtd,
      apy,
      accountStatus,
      interestAccrued,
      cdMaturityDatePvtEncrypt,
      retirementIndicator,
      stkSummary,
    }): Record<string, unknown> => ({
      accountType,
      accountNickname,
      availableBalancePvtEncrypt,
      currentBalancePvtEncrypt,
      interestYtd,
      apy,
      accountStatus,
      interestAccrued,
      cdMaturityDatePvtEncrypt,
      retirementIndicator,
      stkSummary,
    }))(account),
  )
  const stkEligibleAccount = activeInternalAccounts.find(
    account =>
      account.stkSummary &&
      (account.stkSummary.bucketSummary.count > 1 ||
        account.stkSummary.boosterSummary.length > 0),
  )
  const hasSomeOutage =
    outage.isBankOutage(hostData) || outage.isInvestOutage(hostData)
  const shouldShowFullBalanceBreakdown =
    activeInternalAccounts.length > 1 && !hasSomeOutage

  const mortgageStatus = (
    status: AccountMeta['statusReason'],
    isBlocked: boolean,
  ): string => {
    if (isBlocked) {
      return 'Needs Attention'
    }
    switch (status) {
      case 'PAID_IN_FULL':
        return 'Paid'
      case 'TRANSFERRED':
        return 'Resolved'
      case 'FORECLOSED':
        return 'Foreclosed'
      default:
        return 'Active'
    }
  }
  const getHomeLoansStatus = (afgAccts: Account[]): string[] => {
    return afgAccts
      .filter((account: Account) => account.lob === 'MORTGAGE')
      .map((value: Account) => {
        return mortgageStatus(value.meta?.statusReason, isAccountBlocked(value))
      })
  }

  trackPageView({
    eventType: 'pageview',
    pageName: 'AOS:dashboard:Dashboard',
    options: {
      homeLoansStatus: afgAccounts && getHomeLoansStatus(afgAccounts),
      accounts,
      user: {
        id: {
          guid: sessionData.guid || getGuid(hostData),
          cif: sessionData.cif || getCif(hostData),
          scivantageId: hostData.customerInfo.response?.headers.get(
            'scivantageId',
          ),
        },
        type: {
          aaos: sessionData.allyUserRole.aaos,
          bank: sessionData.allyUserRole.bank,
          cc: sessionData.allyUserRole.creditcard,
          investment: sessionData.allyUserRole.investment,
          mortgage: sessionData.allyUserRole.mortgage,
          wealth: sessionData.allyUserRole.wealth,
        },
        attributes: {
          balanceBreakdown: shouldShowFullBalanceBreakdown
            ? 'opened'
            : 'closed',
          clientChannel: isMobile ? 'mobile' : 'desktop',
          isCustomized: isDashboardCustomized ? 'Yes' : 'No',
          isFirstDashboardVisit: getBooleanSessionStorage(
            'isFirstDashboardVisit',
          ),
          isInvest: sessionData.allyUserRole.investment ? 'Yes' : 'No',
          isWealth: sessionData.allyUserRole.wealth ? 'Yes' : 'No',
          isWealthInvest:
            sessionData.allyUserRole.wealth &&
            sessionData.allyUserRole.investment
              ? 'Yes'
              : 'No',
          // do not fix this typo below - the analytics team is looking for this key
          stkElligible: stkEligibleAccount ? 'Yes' : 'No',
        },
      },
    },
  })

  if (isDashboardCustomized) {
    trackCustomizationSettings(
      customizationDomain.data as CustomizationDomain,
      eligibleWidgets,
      dashboardView,
      afgAccounts ? getHomeLoansStatus(afgAccounts) : undefined,
    )
  }
}

export const statusTrackerAnalytics = (
  analytics: Analytics,
  statusTrackerActivity: StatusTrackerActivity[],
): void => {
  const { trackImpression } = analytics
  const types = statusTrackerActivity.map(activity => {
    if (activity.activityName === 'ACHTransfer') return 'ACH Transfer'
    return activity.activityName.split(/(?=[A-Z])/).join(' ')
  })
  const indexTypes = statusTrackerActivity.map((activity, i) => {
    const index = i + 1
    return `${activity.activityName.split(/(?=[A-Z])/).join(' ')} ${index}`
  })
  const isSameType =
    types.length > 1 && types.every((type, i, arr) => type === arr[0])
  const statuses = statusTrackerActivity.map(activity => {
    if (activity.statuses[0].status === 'VerificationRequired')
      return 'Needs Attention'
    return activity.statuses[0].status
  })
  const attention = statuses.includes('Needs Attention') ? 'Yes' : 'No'
  const totalNumber = statusTrackerActivity.length
  const attentionNumber = statuses.filter(
    status => status === 'Needs Attention',
  ).length

  trackImpression({
    eventType: 'impressionEvent',
    impressions: {
      name: 'dashboardImpressionEvent',
      contents: [
        {
          name: 'Status Tracker Impression',
          type: 'statusTracker',
          attributes: {
            type: isSameType ? indexTypes.join(',') : types.join(':'),
            status: statuses.join(':'),
            attention,
            totalNumber,
            attentionNumber,
          },
        },
      ],
    },
  })
}

export const customErrorAnalytics = (
  errorMessage: string,
  errorCode?: string | number,
  attributes?: { [key: string]: string | number },
): void => {
  if ((window as any).allytm) {
    ;(window as any).allytm.event(
      'customError',
      errorMessage,
      errorCode,
      attributes,
    )
  }
}

interface TrackCustomizationModalProps {
  activeTab: 'Organize' | 'Manage'
}

export const trackCustomizationModal = ({
  activeTab,
}: TrackCustomizationModalProps): void => {
  try {
    ;(window as any).allytm.event(
      'modalView',
      `AOS:dashboard:Dashboard:Customize:${activeTab}`,
    )
  } catch (e) {
    logRocket.captureException(e as Error)
  }
}

export const trackBreakdownModal = (): void => {
  try {
    ;(window as any).allytm.event(
      'modalView',
      `AOS:dashboard:Dashboard:Breakdown`,
    )
  } catch (e) {
    logRocket.captureException(e as Error)
  }
}

export const trackHomeLoanModal = (type: string): void => {
  const modalType = (): string => {
    switch (type) {
      case 'needs attention':
        return 'Account Needs Attention'
      case 'paid in full':
        return 'Account is Paid/Resolved'
      case 'transferred':
        return 'Account No Longer Exists'
      default:
        return 'Manage Account'
    }
  }
  try {
    ;(window as any).allytm.event(
      'modalView',
      `AOS:Home Loans:${modalType()}`,
      {
        treatAsPageview: true,
      },
    )
  } catch (e) {
    logRocket.captureException(e as Error)
  }
}
